import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-calculator',
    title: '1. CPQ Engine Implementation',
    desc: 'Implement automated Configure, Price, Quote systems to eliminate manual pricing errors, streamline complex product configurations, and accelerate sales cycles.',
  },
  {
    icon: 'fa-solid fa-users-gear',
    title: '2. CRM & CX Platform Integration',
    desc: 'Unify Salesforce, HubSpot, or Microsoft Dynamics with your core backend ERP to create a 360-degree view of your customer lifecycle.',
  },
  {
    icon: 'fa-solid fa-cart-shopping',
    title: '3. Omnichannel E-Commerce Portals',
    desc: 'Design seamless self-service B2B/B2C portals and guided buying experiences that enable customers to configure and purchase products effortlessly.',
  },
  {
    icon: 'fa-solid fa-tags',
    title: '4. Dynamic Pricing & Yield Management',
    desc: 'Deploy AI-driven dynamic pricing rules, volume discount matrices, and approval workflows to maximize margins and protect revenue streams.',
  },
  {
    icon: 'fa-solid fa-file-contract',
    title: '5. Contract Lifecycle Management (CLM)',
    desc: 'Automate contract generation, e-signatures, redlining, and renewal tracking directly within your sales pipeline.',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: '6. Customer Journey Analytics',
    desc: 'Track sales funnel conversion rates, quoting velocity, customer sentiment, and attrition risks with real-time CX dashboards.',
  },
  {
    icon: 'fa-solid fa-sliders',
    title: '7. Guided Selling & Product Rules',
    desc: 'Build intelligent recommendation engines that empower sales teams and buyers to select optimal product bundles with valid compatibility.',
  },
  {
    icon: 'fa-solid fa-arrows-rotate',
    title: '8. Subscription & Recurring Billing',
    desc: 'Streamline recurring revenue models, usage-based pricing, upsells, and mid-term contract amendments seamlessly.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '9. Omnichannel Customer Support',
    desc: 'Integrate AI chatbots, ticketing systems, and omnichannel contact centers to deliver hyper-responsive post-sale support.',
  },
  {
    icon: 'fa-solid fa-shield-check',
    title: '10. Compliance & Governance Rules',
    desc: 'Enforce strict margin protection policies, regional compliance standards, and multi-tier approval hierarchies on every quote.',
  },
];

const whyChooseUs = [
  'Accelerated quote-to-cash (Q2C) sales cycles',
  'Elimination of manual configuration and pricing errors',
  'Seamless integration between CPQ, CRM, and ERP',
  'Dynamic AI-driven pricing and discount controls',
  'Automated contract generation and e-signature execution',
  'Hyper-personalized guided buying customer experiences',
  'Scalable multi-currency and multi-region pricing support',
  'Real-time visibility into pipeline performance and margins',
  'High adoption rates through user-friendly UI/UX design',
  'End-to-end support from discovery to post-launch optimization',
];

const processSteps = [
  { step: '01', title: 'Sales & Product Discovery', desc: 'Audit current sales workflows, product catalogs, pricing rules, and CX bottlenecks.' },
  { step: '02', title: 'Data & Architecture Design', desc: 'Model complex product dependencies, pricing structures, and CRM/ERP integration layers.' },
  { step: '03', title: 'CPQ & CX Development', desc: 'Configure guided selling rules, custom UI quote builders, and automated workflows.' },
  { step: '04', title: 'System Integration & Testing', desc: 'Connect CPQ to your ERP/CRM platforms and perform end-to-end scenario testing.' },
  { step: '05', title: 'Enablement & Go-Live', desc: 'Train sales teams, roll out the platform, and monitor quote velocity and system adoption.' },
  { step: '06', title: 'Continuous Optimization', desc: 'Refine pricing strategies, UX flows, and product catalog rules using live analytics.' },
];

const techStack = [
  { category: 'CPQ Platforms', items: ['Salesforce CPQ', 'Oracle CPQ', 'Conga CPQ', 'SAP CPQ', 'PROS Smart CPQ'] },
  { category: 'CRM & Customer Experience', items: ['Salesforce Sales/Service Cloud', 'HubSpot', 'Microsoft Dynamics 365', 'Zendesk'] },
  { category: 'ERP & Billing Integration', items: ['SAP S/4HANA', 'Oracle NetSuite', 'Stripe', 'Zuora', 'Workday'] },
  { category: 'Contract & Document Automation', items: ['DocuSign', 'Adobe Sign', 'Conga Composer', 'PandaDoc'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const CustomerExperienceCPQ = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Digital Transformation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Customer Experience & <span className="gradient-text-cyan">CPQ</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we revolutionize your quote-to-cash process and elevate your customer journey. Streamline complex product configurations, automate pricing rules, and deliver frictionless buying experiences that convert leads into loyal customers.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Transform Your Sales Experience &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Customer Experience & CPQ Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">CPQ & CX Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Quote-to-Cash Methodology</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Accelerate Sales and Delight Customers with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Eliminate pricing roadblocks, automate your quote-to-cash workflows, and deliver modern digital buying experiences today.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a CX & CPQ Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomerExperienceCPQ;